import type { ContextFinding, ContextRunDetail } from '@lastrouter/contracts';
import { Alert, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, ChevronRight, Dialog, DialogContent, Field, NativeSelect, RefreshCw, Textarea, X } from '@lastrouter/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { errorMessage, useApiMutation, useApiQuery } from '../../api/hooks.js';
import { useSession } from '../../app/session.js';
import { EmptyState, StatusBadge } from '../../components/common.js';
import { formatDateTime } from '../../lib/format.js';
import type { AssetTab } from './logic.js';
import { CancelRunButton, FindingGroups, PipelineDownloads, PipelineResult, SEVERITIES, SEVERITY_TONE } from './pipeline.js';

/*
 * Gerações do app (pipeline de contexto, ADR 0044) nas abas da página: a execução que as abas mostram,
 * o Resumo, os Riscos e o vazio das abas que dependem de uma geração.
 */

const RUN_TONE = { rodando: 'accent', ok: 'success', falhou: 'danger', interrompida: 'warning' } as const;

/** As gerações do app e a que as abas mostram: a escolhida, senão a última que terminou bem, senão a última. */
export function useAssetRuns(assetId: string) {
  const runs = useApiQuery('contextRuns', { params: { id: assetId } }, { refetchInterval: (q) => (q.state.data?.some((r) => r.status === 'rodando') ? 2000 : false) });
  const [picked, setPicked] = useState<string | null>(null);
  const list = runs.data ?? [];
  // Bateria rodando reabre uma execução que tinha terminado bem: ela continua sendo a que as abas mostram.
  const latestOk = list.find((r) => r.status === 'ok' || (r.status === 'rodando' && r.step === 'bateria')) ?? null;
  const selectedId = picked ?? latestOk?.id ?? list[0]?.id ?? null;
  const selected = useApiQuery(
    'contextRun',
    { params: { id: selectedId ?? '' } },
    { enabled: Boolean(selectedId), refetchInterval: (q) => (q.state.data?.run.status === 'rodando' ? 2000 : false) },
  );
  // A publicação sem validação confere a última execução que terminou bem, como o serviço.
  const ok = useApiQuery('contextRun', { params: { id: latestOk?.id ?? '' } }, { enabled: Boolean(latestOk) });
  const start = useApiMutation('startContextRun', ['contextRuns']);
  // Com o dado anterior mantido na troca de execução, só vale o detalhe da execução escolhida.
  const run = selectedId && selected.data?.run.id === selectedId ? selected.data : null;
  return {
    query: runs,
    list,
    selectedId,
    pick: setPicked,
    run,
    loading: runs.isLoading || (Boolean(selectedId) && !run && !selected.isError),
    running: list.some((r) => r.status === 'rodando'),
    certification: latestOk && ok.data?.pipeline ? ok.data.pipeline.certification : undefined,
    starting: start.isPending,
    /** Notas da última geração, para a próxima começar delas. */
    lastNotes: list.find((r) => r.options.notas)?.options.notas ?? '',
    start: (notes: string, images: string[], onStarted: () => void, onError: (e: unknown) => void) =>
      start.mutate(
        { params: { id: assetId }, body: { options: { alta_cardinalidade: 10_000, ...(notes.trim() ? { notas: notes.trim() } : {}) }, images } },
        {
          onSuccess: () => {
            setPicked(null);
            onStarted();
          },
          onError,
        },
      ),
  };
}
export type AssetRuns = ReturnType<typeof useAssetRuns>;

const MAX_IMAGES = 4;

/** Imagem reduzida no navegador (lado maior até 1600 px, JPEG): print de tela legível e leve para mandar. */
async function shrink(file: Blob): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const g = canvas.getContext('2d')!;
  // Fundo branco: PNG transparente vira JPEG sem fundo preto.
  g.fillStyle = '#fff';
  g.fillRect(0, 0, canvas.width, canvas.height);
  g.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return canvas.toDataURL('image/jpeg', 0.85);
}

/**
 * Botão de gerar: "Gerar contexto" na primeira vez, "Gerar de novo" depois. Só o admin gera. Antes de começar, a
 * pessoa pode contar o que sabe do app; as notas vão para a IA que escreve o significado de cada item.
 */
export function StartButton({ runs, onStarted, primary }: { runs: AssetRuns; onStarted: () => void; primary?: boolean }) {
  const { t } = useTranslation();
  const isAdmin = Boolean(useSession().data?.is_admin);
  const [notes, setNotes] = useState<string | null>(null);
  const [images, setImages] = useState<string[]>([]);
  if (!isAdmin) return null;
  const addImages = async (files: Blob[]) => {
    const room = MAX_IMAGES - images.length;
    if (files.length > room) toast.warning(t('assets.generate.imagesMax', { count: MAX_IMAGES }));
    try {
      const added = await Promise.all(files.slice(0, Math.max(0, room)).map(shrink));
      setImages((cur) => [...cur, ...added].slice(0, MAX_IMAGES));
    } catch {
      toast.error(t('assets.generate.imageUnreadable'));
    }
  };
  const close = () => {
    setNotes(null);
    setImages([]);
  };
  const again = runs.list.length > 0;
  const label = again ? t('assets.generate.startAgain') : t('assets.generate.start');
  return (
    <>
      {/* Depois da primeira geração o botão fica discreto: gerar de novo é raro e refaz a proposta inteira. */}
      <Button variant={!again ? 'primary' : 'secondary'} size={again && !primary ? 'sm' : undefined} disabled={runs.starting || runs.running} onClick={() => setNotes(runs.lastNotes)}>
        <RefreshCw aria-hidden="true" />
        {label}
      </Button>
      {notes !== null ? (
        <Dialog open onOpenChange={(o) => !o && close()}>
          <DialogContent title={label} description={t('assets.generate.notesHelp')} closeLabel={t('common.close')}>
            <form
              className="flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                runs.start(
                  notes,
                  images,
                  () => {
                    close();
                    onStarted();
                  },
                  (err) => toast.error(errorMessage(t, err)),
                );
              }}
            >
              <Field id="gerar-notas" label={`${t('assets.generate.notes')} (${t('common.optional')})`} hint={t('assets.generate.notesHint')}>
                <Textarea
                  rows={7}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  // Print colado (Ctrl+V) vira imagem anexa; texto colado segue normal.
                  onPaste={(e) => {
                    const files = [...e.clipboardData.files].filter((f) => f.type.startsWith('image/'));
                    if (!files.length) return;
                    e.preventDefault();
                    void addImages(files);
                  }}
                  maxLength={4000}
                  autoFocus
                />
              </Field>
              <Field id="gerar-imagens" label={`${t('assets.generate.images')} (${t('common.optional')})`} hint={t('assets.generate.imagesHint', { count: MAX_IMAGES })}>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  multiple
                  disabled={images.length >= MAX_IMAGES}
                  className="text-[length:var(--lr-text-sm)] file:mr-3 file:rounded-[var(--lr-radius-md)] file:border file:border-border file:bg-surface file:px-3 file:py-1.5 file:text-fg"
                  onChange={(e) => {
                    void addImages([...(e.target.files ?? [])]);
                    e.target.value = '';
                  }}
                />
              </Field>
              {images.length ? (
                <ul className="flex flex-wrap gap-2" aria-label={t('assets.generate.images')}>
                  {images.map((src, i) => (
                    <li key={src.slice(-40) + i} className="relative">
                      <img src={src} alt={t('assets.generate.imageN', { n: i + 1 })} className="h-20 w-28 rounded-[var(--lr-radius-md)] border border-border object-cover" />
                      <Button
                        size="sm"
                        variant="secondary"
                        className="absolute right-1 top-1 h-6 px-1"
                        aria-label={t('assets.generate.imageRemove', { n: i + 1 })}
                        onClick={() => setImages((cur) => cur.filter((_, j) => j !== i))}
                      >
                        <X />
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <div className="flex justify-end gap-2">
                <Button onClick={close}>{t('common.cancel')}</Button>
                <Button type="submit" variant="primary" disabled={runs.starting}>
                  <RefreshCw aria-hidden="true" />
                  {label}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}

/** Vazio das abas que dependem de uma geração do pipeline. */
export function NeedsRun({ runs, onTab }: { runs: AssetRuns; onTab: (t: AssetTab) => void }) {
  const { t } = useTranslation();
  const r = runs.run;
  const why = !r ? 'none' : r.run.status !== 'ok' ? 'unfinished' : 'old';
  return (
    <Card>
      <CardContent className="flex flex-col items-start gap-3">
        <p className="text-fg-secondary">{t(`assets.needsRun.${why}`)}</p>
        {why === 'unfinished' ? (
          <Button onClick={() => onTab('summary')}>
            {t('assets.goTo', { tab: t('assets.tabs.summary') })}
            <ChevronRight aria-hidden="true" />
          </Button>
        ) : (
          <StartButton runs={runs} onStarted={() => onTab('summary')} primary />
        )}
      </CardContent>
    </Card>
  );
}

function Finding({ f, runId }: { f: ContextFinding; runId?: string }) {
  const { t } = useTranslation();
  const k = 'assets.generate.risk';
  const accept = useApiMutation('acceptRisk', ['contextRun']);
  // Motivo sendo escrito (null: formulário fechado).
  const [reason, setReason] = useState<string | null>(null);
  const send = (accepted: boolean) =>
    accept.mutate(
      { params: { id: runId!, risk: f.id }, body: { accepted, reason: reason ?? '' } },
      {
        onSuccess: () => {
          setReason(null);
          toast.success(t(accepted ? `${k}.acceptedToast` : `${k}.reopenedToast`));
        },
        onError: (err) => toast.error(errorMessage(t, err)),
      },
    );
  return (
    <li className="flex flex-col gap-1 border-b border-border py-3 last:border-0">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge tone={f.accepted ? 'neutral' : SEVERITY_TONE[f.severity]}>{t(`assets.generate.severity.${f.severity}`)}</StatusBadge>
        {f.accepted ? <StatusBadge tone="success">{t(`${k}.accepted`)}</StatusBadge> : null}
        <span className="font-medium">{f.title}</span>
      </div>
      <ul className="list-disc pl-5 text-[length:var(--lr-text-sm)] text-fg-secondary">
        {f.evidence.map((e) => (
          <li key={e}>{e}</li>
        ))}
      </ul>
      <p className="text-[length:var(--lr-text-sm)]">
        <span className="font-medium">{t('assets.generate.impact')}:</span> {f.impact}
      </p>
      <p className="text-[length:var(--lr-text-sm)]">
        <span className="font-medium">{t('assets.generate.recommendation')}:</span> {f.recommendation}
      </p>
      {f.accepted ? (
        <div className="flex flex-wrap items-center gap-2 text-[length:var(--lr-text-sm)] text-fg-secondary">
          <span>{t(`${k}.acceptedBy`, { by: f.accepted.by, when: formatDateTime(f.accepted.at), reason: f.accepted.reason })}</span>
          {runId ? (
            <Button size="sm" variant="ghost" disabled={accept.isPending} onClick={() => send(false)}>
              {t(`${k}.reopen`)}
            </Button>
          ) : null}
        </div>
      ) : runId && reason === null ? (
        <div>
          <Button size="sm" variant="secondary" onClick={() => setReason('')}>
            {t(`${k}.accept`)}
          </Button>
        </div>
      ) : runId ? (
        <div className="flex flex-col gap-2 pt-1">
          <Field id={`risk-reason-${f.id}`} label={t(`${k}.reason`)} hint={t(`${k}.reasonHint`)}>
            <Textarea rows={2} value={reason ?? ''} onChange={(e) => setReason(e.target.value)} maxLength={2000} />
          </Field>
          <div className="flex gap-2">
            <Button size="sm" disabled={accept.isPending || (reason ?? '').trim().length < 10} onClick={() => send(true)}>
              {t(`${k}.confirm`)}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setReason(null)}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

function RunSteps({ run }: { run: ContextRunDetail }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle>{run.model?.title ?? t('assets.generate.run')}</CardTitle>
        <CardDescription>
          <span className="block">{t('assets.generate.startedBy', { user: run.run.started_by, when: formatDateTime(run.run.started_at) })}</span>
          {run.model ? <span className="block">{t('assets.generate.modelCounts', run.model)}</span> : null}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ol className="flex flex-col gap-1 text-[length:var(--lr-text-sm)]">
          {run.run.steps.map((s) => (
            <li key={s.name} className="flex flex-wrap items-center gap-2">
              <StatusBadge tone={RUN_TONE[s.status]}>{t(`assets.generate.stepStatus.${s.status}`)}</StatusBadge>
              <span className="font-medium">{t(`assets.generate.steps.${s.name}`, { defaultValue: s.name })}</span>
              {s.detail ? <span className="min-w-0 break-words text-fg-secondary">{s.detail}</span> : null}
            </li>
          ))}
        </ol>
        {run.run.status === 'rodando' ? (
          <div className="mt-3 flex flex-col items-start gap-2">
            <p className="text-[length:var(--lr-text-sm)] text-fg-secondary">{t('assets.generate.runningHint')}</p>
            <CancelRunButton runId={run.run.id} />
          </div>
        ) : null}
        {/* O serviço escreve "cancelada por <quem>": cancelar não é erro, não pede suporte. */}
        {run.run.error && /cancelad[ao] por/.test(run.run.error) ? (
          <Alert tone="warning" className="mt-3" title={t('assets.generate.cancelledTitle')}>
            <p className="break-words">{run.run.error}</p>
          </Alert>
        ) : run.run.error ? (
          <Alert
            tone="danger"
            className="mt-3"
            title={t(`assets.generate.errorTitle.${run.run.steps.find((s) => s.status === 'falhou')?.name ?? 'other'}`, {
              defaultValue: t('assets.generate.errorTitle.other'),
            })}
          >
            <p>{t('assets.generate.errorHelp')}</p>
            <p className="mt-1 break-words font-mono text-[length:var(--lr-text-xs)]">{run.run.error}</p>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** O próximo passo em destaque no resumo, com o atalho para a aba dele. */
function NextStep({ tab, onTab }: { tab: AssetTab; onTab: (s: AssetTab) => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3 rounded-[var(--card-radius)] border border-accent bg-accent-soft p-4 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {/* O rótulo "Próximo passo" entra no título: nada de linha pequena em caixa alta em cima dele. */}
        <span className="font-semibold text-fg">{t('assets.nextStepTo', { tab: t(`assets.tabs.${tab}`) })}</span>
        <span className="text-[length:var(--lr-text-sm)] text-fg-secondary">{t(`assets.nextHint.${tab}`)}</span>
      </div>
      <div>
        <Button variant="primary" onClick={() => onTab(tab)}>
          {t('assets.goTo', { tab: t(`assets.tabs.${tab}`) })}
          <ChevronRight aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

/** Uma geração que não é a mostrada nas abas: a que está rodando ou a última, que falhou. */
function LatestRun({ id }: { id: string }) {
  const q = useApiQuery('contextRun', { params: { id } }, { refetchInterval: (query) => (query.state.data?.run.status === 'rodando' ? 2000 : false) });
  return q.data?.run.id === id ? <RunSteps run={q.data} /> : null;
}

/** Aba Resumo: situação, próximo passo, gerar (de novo), os números da geração e os arquivos para baixar. */
export function SummaryTab({ runs, next, onTab }: { runs: AssetRuns; next: AssetTab; onTab: (t: AssetTab) => void }) {
  const { t } = useTranslation();
  const { list, run } = runs;
  const latest = list[0];
  const isAdmin = Boolean(useSession().data?.is_admin);
  return (
    <div className="flex flex-col gap-4">
      {next !== 'summary' ? <NextStep tab={next} onTab={onTab} /> : null}
      <Card>
        <CardContent className="flex flex-col gap-3">
          <p className="text-[length:var(--lr-text-sm)] text-fg-secondary">{t('assets.generate.help')}</p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            {list.length > 1 ? (
              <div className="min-w-0 flex-1">
                <Field id="gerar-execucao" label={t('assets.generate.runs')} hint={t('assets.generate.runsHint')}>
                  <NativeSelect value={runs.selectedId ?? ''} onChange={(e) => runs.pick(e.target.value)}>
                    {list.map((r) => (
                      <option key={r.id} value={r.id}>
                        {`${formatDateTime(r.started_at)} (${r.started_by}, ${t(`assets.generate.runStatus.${r.status}`)})`}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
              </div>
            ) : null}
            {list.length === 0 ? (
              <div>
                <StartButton runs={runs} onStarted={() => onTab('summary')} />
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>
      {list.length === 0 ? <EmptyState>{t('assets.generate.none')}</EmptyState> : null}
      {latest && latest.id !== runs.selectedId ? <LatestRun id={latest.id} /> : null}
      {run ? <RunSteps run={run} /> : null}
      {run?.pipeline ? (
        <>
          <PipelineResult p={run.pipeline} />
          <PipelineDownloads runId={run.run.id} p={run.pipeline} />
        </>
      ) : null}
      {list.length && isAdmin ? (
        <details className="rounded-[var(--card-radius)] border border-border bg-surface px-[var(--card-padding)] py-3">
          <summary className="cursor-pointer font-medium">{t('assets.generate.againTitle')}</summary>
          <div className="mt-3 flex flex-col items-start gap-3">
            <p className="text-[length:var(--lr-text-sm)] text-fg-secondary">{t('assets.generate.againHelp')}</p>
            <StartButton runs={runs} onStarted={() => onTab('summary')} />
          </div>
        </details>
      ) : null}
    </div>
  );
}

/** Aba Riscos: os achados da geração. */
export function RisksTab({ runs, onTab }: { runs: AssetRuns; onTab: (t: AssetTab) => void }) {
  const { t } = useTranslation();
  const run = runs.run;
  if (!run || run.run.status !== 'ok') return <NeedsRun runs={runs} onTab={onTab} />;
  const findings = run.findings ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('assets.generate.findings', { n: findings.length })}</CardTitle>
        <CardDescription>{t('assets.generate.findingsHelp')}</CardDescription>
      </CardHeader>
      <CardContent>
        {findings.length === 0 ? (
          <EmptyState>{t('assets.generate.noFindings')}</EmptyState>
        ) : run.pipeline ? (
          <FindingGroups findings={findings} render={(f) => <Finding key={f.id} f={f} runId={run.run.id} />} />
        ) : (
          <ul>
            {[...findings]
              .sort((a, b) => SEVERITIES.indexOf(a.severity) - SEVERITIES.indexOf(b.severity))
              .map((f) => (
                <Finding key={f.id} f={f} />
              ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
